import {
  ErrorComponentProps,
  Outlet,
  createRootRouteWithContext,
  redirect
} from "@tanstack/react-router";
import { useEffect, useState } from "react";
import type { RouterContext } from "@/lib/router-context";
import { Toaster } from "@/components/ui/sonner";
import NoConnection from "@/components/auth/NoConnection";
import NotFound from "@/components/auth/NotFound";
import { Button } from "@/components/ui/button";
import {
  isAuthPath,
  isProtectedPath,
  resolvePostAuthDestination
} from "@/lib/route-access";

export const Route = createRootRouteWithContext<RouterContext>()({
  beforeLoad: async ({ context, location }) => {
    const pathname = location.pathname;
    const href =
      typeof location.href === "string" && location.href.length > 0
        ? location.href
        : `${location.pathname}${typeof location.search === "string" ? location.search : ""}`;
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    const searchParams = new URL(href, base).searchParams;
    const previewOnboarding =
      import.meta.env.DEV &&
      pathname === "/onboarding" &&
      (searchParams.get("preview") === "1" || searchParams.get("preview") == null);
    const forceOnboarding =
      import.meta.env.DEV &&
      searchParams.get("forceOnboarding") === "1";
    const needsAuthCheck =
      isAuthPath(pathname) || isProtectedPath(pathname) || pathname === "/onboarding";

    if (!needsAuthCheck) {
      return;
    }

    if (previewOnboarding) {
      return;
    }

    const session = await context.ensureSession();

    if (!session && (isProtectedPath(pathname) || pathname === "/onboarding")) {
      throw redirect({
        to: "/auth",
        search: {
          redirect: location.href
        }
      });
    }

    if (!session) {
      return;
    }

    if (isAuthPath(pathname)) {
      throw redirect({
        to: resolvePostAuthDestination(session, "/dashboard")
      });
    }

    if (pathname === "/onboarding") {
      if (!forceOnboarding && session.onboardingComplete) {
        throw redirect({
          to: "/dashboard"
        });
      }
    }
  },
  component: RootComponent,
  notFoundComponent: NotFound,
  errorComponent: ErrorComponent
});

function RootComponent() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  // if (!isOnline) {
  //   return <NoConnection />;
  // }

  return (
    <>
      <Outlet />
      <Toaster position="top-right" />
    </>
  );
}

function ErrorComponent({ error }: ErrorComponentProps) {
  const isNetworkError =
    !navigator.onLine ||
    error.message.includes("Failed to fetch") ||
    error.message.includes("Network request failed") ||
    error.message.includes("NetworkError") ||
    error.message === "NO_CONNECTION";

  if (isNetworkError) {
    return <NoConnection />;
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-bold">Something went wrong</h1>
        <p className="text-muted-foreground mt-2">{error.message}</p>
        <Button
          className="mt-4 px-5"
          onClick={() => (window.location.href = "/")}
        >
          Back to Dashboard
        </Button>
      </div>
    </div>
  );
}
